import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { AddressModel } from '../../components/partials/address/models/address.model';
import { BaseEntityResource } from '../../core/abstractions/base-entity.resource';
import { ContactInformationModel } from '../../models/contact-information.model';
import { ActionResultModel } from '../../models/core/action-result.model';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { PostValueResponseModel } from '../../models/core/post-value-response.model';
import { ResourceConfig } from '../../models/core/resource.config';
import { DeliveryMethodModel } from '../../models/delivery-method.model';
import { ListShareholderModel } from '../../models/list-shareholder.model';
import { ShareholderAccountModel } from '../../models/shareholder-account.model';
import { ShareholderInfomaterialModel } from '../../models/shareholder-infomaterial.model';
import { ShareholderSummaryModel } from '../../models/shareholder-summary.model';
import { ShareholderModel } from '../../models/shareholder.model';
import { ShareholdersShareInfoModel } from '../../models/shareholders-share-info.model';
import { TopShareholderModel } from '../../models/top-shareholder.model';
import { TransactionModel } from '../../models/transaction.model';

@Injectable({
    providedIn: 'root'
})
export class ShareholdersResource extends BaseEntityResource<ShareholderModel, ShareholderSummaryModel, ListShareholderModel> {
    constructor() {
        super(new ResourceConfig({ baseController: 'shareholders' }));
    }

    /**
     * Gets the top n shareholders sorted by amount of shares
     * @param quantity Number of top items to be returned.
     */
    getTop(quantity: number): Observable<ApiResponseModel<TopShareholderModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<TopShareholderModel[]>>(this.getUrl(`top/${quantity}`));
    }

    /**
     * Gets the accounts of a specific shareholder.
     * @param shareholderId ID of the shareholder.
     */
    getAccounts(shareholderId: string): Observable<ApiResponseModel<ShareholderAccountModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<ShareholderAccountModel[]>>(this.getUrl(`${shareholderId}/accounts`));
    }

    /**
     * Gets the transactions of a specific shareholder
     * @param shareholderId ID of the shareholder.
     */
    getTransactions(shareholderId: string): Observable<ApiResponseModel<TransactionModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<TransactionModel[]>>(this.getUrl(`${shareholderId}/transactions`));
    }

    /**
     * Returns share statistics for a specific shareholder.
     * @param shareholderId Shareholder ID.
     */
    getShareStatistics(shareholderId: string): Observable<ApiResponseModel<ShareholdersShareInfoModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<ShareholdersShareInfoModel[]>>(
            this.getUrl(`${shareholderId}/share-statistics`)
        );
    }

    /**
     * Updates the address of a shareholder.
     * @param shareholderId Shareholder ID.
     * @param shareholderAddress New address of the shareholder.
     */
    updateAddress(shareholderId: string, shareholderAddress: AddressModel): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePut<ApiResponseModel<PostValueResponseModel>>(
            this.getUrl(`${shareholderId}/address`),
            shareholderAddress
        );
    }

    /**
     * Gets the address of a shareholder.
     * @param shareholderId Shareholder ID.
     */
    getAddress(shareholderId: string): Observable<ApiResponseModel<AddressModel>> {
        return this._requestService.makeGet<ApiResponseModel<AddressModel>>(this.getUrl(`${shareholderId}/address`));
    }

    /**
     * Confirms and saves the current authenticated shareholder's email address or mobile number.
     * If no confirmation code is provided, the code is sent to the email or mobile. If there is a code provided,
     * the code is validated and if valid the email or mobile is updated and confirmed for the given shareholder.
     */
    updateContactInformation(data: ContactInformationModel): Observable<ApiResponseModel<ActionResultModel>> {
        return this._requestService.makePost<ApiResponseModel<ActionResultModel>>(this.getUrl('current/confirm-email-or-mobile'), data);
    }

    /**
     * Retrieves the current shareholder's delivery method.
     */
    getDeliveryMethodForCurrent(): Observable<ApiResponseModel<DeliveryMethodModel>> {
        return this._requestService.makeGet<ApiResponseModel<DeliveryMethodModel>>(this.getUrl('current/delivery-method'));
    }

    /**
     * Sets the current shareholder's delivery method.
     */
    setDeliveryMethodForCurrent(deliveryMethodId: string): Observable<ApiResponseModel<DeliveryMethodModel>> {
        return this._requestService.makePost<ApiResponseModel<DeliveryMethodModel>>(
            this.getUrl(`current/delivery-method?deliveryMethodId=${deliveryMethodId}`)
        );
    }

    /**
     * Retrieves the current shareholder's infomaterial.
     */
    getInfomaterialForCurrent(): Observable<ApiResponseModel<ShareholderInfomaterialModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<ShareholderInfomaterialModel[]>>(this.getUrl('current/infomaterial'));
    }

    /**
     * Selects the informed infomaterial for the current shareholder.
     */
    selectInfomaterialForCurrent(id: string): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makePost<ApiResponseModel<PostValueResponseModel>>(this.getUrl(`current/infomaterial/${id}`));
    }

    /**
     * Unselects the informed infomaterial for the current shareholder.
     */
    unselectInfomaterialForCurrent(id: string): Observable<ApiResponseModel<PostValueResponseModel>> {
        return this._requestService.makeDelete<ApiResponseModel<PostValueResponseModel>>(this.getUrl('current/infomaterial'), id);
    }
}
